import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezonePlugin from "dayjs/plugin/timezone";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(utc);
dayjs.extend(timezonePlugin);
dayjs.extend(relativeTime);

/**
 * Convert UTC ISO string to a tenant-localized display string.
 */
export function formatInTz(iso, tz = "UTC", pattern = "DD MMM YYYY, HH:mm") {
  if (!iso) return "";
  return dayjs.utc(iso).tz(tz).format(pattern);
}

export function relativeFromNow(iso) {
  if (!iso) return "";
  return dayjs.utc(iso).fromNow();
}

/**
 * Convert a local datetime-local input (in tenant tz) back to UTC ISO for API.
 */
export function localInputToUtc(localDatetimeStr, tz = "UTC") {
  if (!localDatetimeStr) return null;
  return dayjs.tz(localDatetimeStr, tz).utc().toISOString();
}

/**
 * Convert UTC ISO to a value suitable for <input type="datetime-local">
 * in tenant tz.
 */
export function utcToLocalInput(iso, tz = "UTC") {
  if (!iso) return "";
  return dayjs.utc(iso).tz(tz).format("YYYY-MM-DDTHH:mm");
}

export const COMMON_TIMEZONES = [
  "UTC",
  "Europe/Istanbul",
  "Europe/London",
  "Europe/Berlin",
  "America/New_York",
  "America/Los_Angeles",
  "Asia/Dubai",
  "Asia/Tokyo",
  "Australia/Sydney",
];

/**
 * Sensible default value for a fresh datetime-local input when no
 * date is set yet: today in the tenant tz at 17:00. Used by inline
 * date pickers and subtask composers so users only need to click Save
 * to pin "today" as a due date (Linear/Todoist-style UX).
 */
export function todayLocalInputDefault(tz) {
  const zone = tz || "UTC";
  try {
    return dayjs().tz(zone).hour(17).minute(0).second(0).format("YYYY-MM-DDTHH:mm");
  } catch {
    return dayjs().hour(17).minute(0).format("YYYY-MM-DDTHH:mm");
  }
}

/**
 * Classify a due date (UTC ISO) into a date group relative to tenant tz "now".
 * Buckets: overdue | today | this_week | later | no_date.
 * `status` allows overdue-done tasks to be lumped into their real bucket (today/this_week/later).
 */
export function dateGroupOf(iso, tz = "UTC", status = "todo") {
  if (!iso) return "no_date";
  const now = dayjs().tz(tz);
  const d = dayjs.utc(iso).tz(tz);
  if (d.isBefore(now, "day") && status !== "done") return "overdue";
  if (d.isSame(now, "day")) return "today";
  const endOfWeek = now.endOf("week");
  if (d.isBefore(endOfWeek) || d.isSame(endOfWeek, "day")) return "this_week";
  return "later";
}

/**
 * Human-friendly badge for a due date. Returns { key, tone, timePart, daysAgo }
 * — the caller localizes via i18n and formats time.
 *  tone ∈ red | amber | blue | zinc
 *  key  ∈ overdue | today | tomorrow | this_week | later | done
 */
export function dueBadge(iso, tz = "UTC", status = "todo") {
  if (!iso) return null;
  const now = dayjs().tz(tz);
  const d = dayjs.utc(iso).tz(tz);
  const diffDays = d.startOf("day").diff(now.startOf("day"), "day");
  const timePart = d.format("HH:mm");
  if (status === "done") {
    return { key: "done", tone: "zinc", timePart, dateStr: d.format("DD MMM") };
  }
  if (diffDays < 0) {
    return {
      key: "overdue",
      tone: "red",
      timePart,
      dateStr: d.format("DD MMM"),
      daysAgo: Math.abs(diffDays),
    };
  }
  if (diffDays === 0) return { key: "today", tone: "amber", timePart, dateStr: "" };
  if (diffDays === 1) return { key: "tomorrow", tone: "blue", timePart, dateStr: "" };
  if (diffDays <= 6) return { key: "this_week", tone: "blue", timePart, dateStr: d.format("ddd") };
  return { key: "later", tone: "zinc", timePart, dateStr: d.format("DD MMM") };
}
